import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { styles as spotlightStyles } from "../../utils/spotlight";

import componentStyles from "./literary-design.css";

import type { PropertyValueMap } from "lit";
import type { Ref } from "lit/directives/ref.js";

const ELEMENT_NAME = "bp-literary-design";

const USX_ELEMENTS = new Set([
  "book",
  "cell",
  "chapter",
  "char",
  "figure",
  "group",
  "note",
  "optbreak",
  "para",
  "ref",
  "row",
  "sidebar",
  "table",
  "usx",
  "verse",
]);

@customElement(ELEMENT_NAME)
export class LiteraryDesign extends LitElement {
  // private contentNode?: Node;

  static styles = [spotlightStyles, componentStyles];

  @property({ attribute: "title" })
  designTitle?: string;

  @property({ attribute: "excluded-key-paths" })
  excludedKeyPaths: string = "[]";

  @property()
  reference: string | undefined;

  @property({ attribute: "usx" })
  usx?: string;

  private contentRef: Ref<HTMLDivElement> = createRef();

  firstUpdated(
    changedProperties: Map<PropertyKey, unknown> | PropertyValueMap<this>,
  ): void {
    if (changedProperties.has("usx") && this.usx) {
      this.parseScript(this.usx);
    }
  }

  getElementById(id: string): HTMLElement | null {
    return this.shadowRoot?.querySelector(`[data-id="${id}"]`) ?? null;
  }

  getElementByKeyPath(keyPath: string): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector(`[data-key-path="${keyPath}"]`) ?? null
    );
  }

  parseScript(script: string) {
    const parser = new DOMParser();
    const xmlDocument = parser.parseFromString(script ?? "", "text/xml");
    const usx = xmlDocument.firstElementChild;

    if (!usx || usx.tagName !== "usx") {
      throw new Error(`${ELEMENT_NAME}: Missing required <usx> tag`);
    }
    const root = map(
      usx,
      (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (USX_ELEMENTS.has(node.nodeName)) {
            return document.createElement(`${ELEMENT_NAME}-${node.nodeName}`);
          }
        }

        return node.cloneNode();
      },
      (_, attr) => {
        if (attr.name === "id") {
          return createAttribute("data-id", attr.value);
        }

        if (attr.name === "style") {
          return createAttribute(
            "data-style",
            attr.value
              .split(" ")
              // Styles are wrapped in _ to allow using attribute selectors to
              // accurately select single letter styles using the * selector.
              // For example, style="ord bk" would be incorrectly matched by [data-style*="k"]
              // if you were only trying to select styles with a single "k" such as style="ord k".
              .map((s: string) => `_${s}_`)
              .join(" "),
          );
        }

        return attr.cloneNode(true) as Attr;
      },
    );
    decorateElementKeyPath(root, `${ELEMENT_NAME}-group`.toUpperCase());
    this.contentRef?.value?.lastChild?.remove();
    this.contentRef?.value?.appendChild(root);
  }

  removeElementByKeyPath(keyPath: string): void {
    const element = this.shadowRoot?.querySelector(
      `[data-key-path="${keyPath}"]`,
    );

    if (!element) return;
    element.classList.add("hidden-group");

    if (
      element.nextElementSibling?.nodeName === "BP-LITERARY-DESIGN-OPTBREAK"
    ) {
      element.nextElementSibling.classList.add("hidden-group");
    }

    if (
      element.previousElementSibling?.nodeName === "BP-LITERARY-DESIGN-OPTBREAK"
    ) {
      element.previousElementSibling.classList.add("hidden-group");
    }
  }

  render() {
    return html`
      <div>
        ${this.designTitle
          ? html`<div class="title">${this.designTitle}</div>`
          : html`<div class="design-start"></div>`}
        ${this.reference
          ? html`<div class="reference">${this.reference}</div>`
          : null}
        <div ${ref(this.contentRef)}></div>
        <slot name="caption"></slot>
      </div>
    `;
  }

  updated() {
    this.shadowRoot
      ?.querySelectorAll(".hidden-group")
      .forEach((query) => query.classList.remove("hidden-group"));
    console.log("Excluded paths", this.excludedKeyPaths);
    const keyPathsArray: Array<string> =
      this.excludedKeyPaths
        .match(/\[(?<stringArray>.*)\]/)
        ?.groups?.stringArray.split(",")
        .map((arrayValue) => arrayValue.trim()) ?? [];
    keyPathsArray.forEach((path) => this.removeElementByKeyPath(path));
  }

  willUpdate(
    changedProperties: Map<PropertyKey, unknown> | PropertyValueMap<this>,
  ): void {
    if (changedProperties.has("usx")) {
      this.parseScript(this.usx ?? "");
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-literary-design": LiteraryDesign;
  }
}

window.customElements.define(
  `${ELEMENT_NAME}-group`,
  class extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute("title")) {
        this.style.setProperty(
          "--group-title-height",
          window.getComputedStyle(this, ":before").height,
        );
      }

      if (this.hasAttribute("sidebar-value")) {
        const element = document.createElement(
          `${ELEMENT_NAME}-group-sidebar-value`,
        );
        element.innerText = this.getAttribute("sidebar-value") ?? "";
        this.insertBefore(element, this.firstChild);
      }
    }
  },
);

window.customElements.define(
  `${ELEMENT_NAME}-verse`,
  class extends HTMLElement {
    connectedCallback() {
      const verse = this.getAttribute("number") ?? "";
      this.innerText = verse;
    }
  },
);

window.customElements.define(
  `${ELEMENT_NAME}-char`,
  class extends HTMLElement {
    connectedCallback() {
      const wordLang = this.getAttribute("word-lang");
      const wordOriginal = this.getAttribute("word-original");
      const wordTransliteration = this.getAttribute("word-transliteration");

      if (wordOriginal || wordTransliteration) {
        const html: string[] = [];

        if (wordOriginal) {
          html.push(
            `<bp-text-${wordLang}>${wordOriginal}</bp-text-${wordLang}>`,
          );
        }

        if (wordTransliteration) {
          html.push(
            `<${ELEMENT_NAME}-word-transliteration>${wordTransliteration}</${ELEMENT_NAME}-word-transliteration>`,
          );
        }

        if (html.length) {
          const element = document.createElement(`${ELEMENT_NAME}-word`);
          element.innerHTML = `&nbsp;(${html.join(" • ")})`;
          this.appendChild(element);
        }
      }
      const highlightShown = this.getAttribute("highlight-show");
      const highlightColor = this.getAttribute("highlight-color");
      const highlightRevealTime = this.getAttribute("highlight-reveal-time");

      if (highlightShown || highlightRevealTime || highlightColor) {
        const element = document.createElement("bp-highlight");

        if (highlightRevealTime) {
          element.setAttribute("reveal-time", highlightRevealTime);
        }

        if (highlightColor) {
          element.setAttribute("color", highlightColor);
        }

        element.setAttribute("can-spotlight", "true");
        element.setAttribute("show", highlightShown ?? "false");
        element.setAttribute("data-id", this.getAttribute("data-id") ?? "");
        this.setAttribute("data-id", "");
        for (const node of Array.from(this.childNodes)) {
          element.appendChild(node);
        }
        this.appendChild(element);
      }
      const markVariant = this.getAttribute("mark-variant");
      const markColor = this.getAttribute("mark-color");

      if (markVariant || markColor) {
        const element = document.createElement("bp-mark");
        element.setAttribute("color", markColor ?? "");
        element.setAttribute("variant", markVariant ?? "");
        for (const node of Array.from(this.childNodes)) {
          element.appendChild(node);
        }
        this.appendChild(element);
      }
    }
  },
);

function createAttribute(name: string, value: string) {
  const attr = document.createAttribute(name);
  attr.value = value;

  return attr;
}

function decorateElementKeyPath(rootNode: Node, elementName: string) {
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_ELEMENT);
  let currentParentNode: Node | null = null;
  let currentRootParentNode: Node | null = null;
  let keyPath: { value: number }[] = [];
  const keyPathIndexes = new Map<Node, number>();
  const keyPathString = () => keyPath.map((k) => k.value).join(".");
  const dataIdString = () => "group-" + keyPath.map((k) => k.value).join("-");
  while (walker.nextNode()) {
    if (walker.currentNode.nodeName !== elementName) continue;
    const parentNode = walker.currentNode.parentNode;

    if (!currentParentNode || parentNode !== currentParentNode) {
      if (parentNode && keyPathIndexes.has(parentNode)) {
        keyPath = keyPath.slice(0, (keyPathIndexes.get(parentNode) ?? 0) + 1);
        keyPath[keyPath.length - 1].value += 1;
      } else {
        if (
          !currentRootParentNode ||
          !currentRootParentNode.contains(walker.currentNode)
        ) {
          if (keyPath.length) {
            keyPath = keyPath.slice(0, 1);
            keyPath[keyPath.length - 1].value += 1;
          } else {
            keyPath.push({ value: 1 });
          }
          currentRootParentNode = parentNode;
        } else {
          keyPath.push({ value: 1 });
        }

        if (parentNode) {
          keyPathIndexes.set(parentNode, keyPath.length - 1);
        }
      }
      currentParentNode = parentNode;
    } else {
      keyPath[keyPath.length - 1].value += 1;
    }
    (walker.currentNode as HTMLElement).setAttribute(
      "data-key-path",
      keyPathString(),
    );
    (walker.currentNode as HTMLElement).setAttribute("data-id", dataIdString());
  }
}

function map(
  node: Node,
  mapNode: (node: Node) => Node,
  mapAttribute: (node: Node, attr: Attr) => Attr | null,
): Node {
  const newNode = mapNode(node);
  node.childNodes.forEach((childNode) => {
    newNode.appendChild(map(childNode, mapNode, mapAttribute));
  });

  if (
    node.nodeType === Node.ELEMENT_NODE &&
    newNode.nodeType === Node.ELEMENT_NODE
  ) {
    const nodeElement = node as Element;
    const newNodeElement = newNode as Element;
    for (let i = 0, l = nodeElement.attributes.length; i < l; i++) {
      const attr = nodeElement.attributes.item(i);

      if (!attr) continue;
      const newAttr = mapAttribute(node, attr);

      if (!newAttr) continue;
      newNodeElement.setAttributeNode(newAttr);
    }
  }

  return newNode;
}
